import { Col, Flex, Row, Typography } from "antd"
import cls from "classnames"

import "./styles.less"

const { Text } = Typography

const RISK_LEVELS = {
  VERY_LOW: "very_low",
  LOW: "low",
  MEDIUM: "medium",
  HIGH: "high",
  VERY_HIGH: "very_high"
}

const RISK_DESCRIPTIONS = {
  [RISK_LEVELS.VERY_LOW]:
    "Excellent performance. Your chargeback rate is well within the safe zone.",
  [RISK_LEVELS.LOW]:
    "Slightly elevated, but still acceptable. Keep monitoring and optimize where possible.",
  [RISK_LEVELS.MEDIUM]: "Your chargeback rate is above optimal, bringing it down is advised.",
  [RISK_LEVELS.HIGH]:
    "Immediate attention needed. Chargeback rate is putting your account at risk.",
  [RISK_LEVELS.VERY_HIGH]:
    "Severe. You risk account suspension, withheld payouts, or removal from card networks."
}

const RISK_LEVEL_TEXT = {
  [RISK_LEVELS.VERY_LOW]: "Very low risk",
  [RISK_LEVELS.LOW]: "Low risk",
  [RISK_LEVELS.MEDIUM]: "Medium risk",
  [RISK_LEVELS.HIGH]: "High risk",
  [RISK_LEVELS.VERY_HIGH]: "Very high risk"
}

const getRiskTextColor = (currentRiskLevel) => {
  switch (currentRiskLevel) {
    case RISK_LEVELS.VERY_LOW:
    case RISK_LEVELS.LOW:
      return "success"
    case RISK_LEVELS.MEDIUM:
      return "warning"
    case RISK_LEVELS.HIGH:
    case RISK_LEVELS.VERY_HIGH:
      return "danger"
    default:
      return "success"
  }
}

const getRiskLevel = (riskValue) => {
  if (riskValue >= 0 && riskValue <= 0.25) return RISK_LEVELS.VERY_LOW
  if (riskValue >= 0.251 && riskValue <= 0.45) return RISK_LEVELS.LOW
  if (riskValue >= 0.451 && riskValue <= 0.8) return RISK_LEVELS.MEDIUM
  if (riskValue >= 0.801 && riskValue <= 1.5) return RISK_LEVELS.HIGH
  if (riskValue >= 1.51) return RISK_LEVELS.VERY_HIGH
  return RISK_LEVELS.VERY_LOW
}

const RiskBar = ({ riskValue = 0, riskStatus, isEmpty }) => {
  const currentRiskLevel = riskStatus || getRiskLevel(riskValue)

  const getActiveBarPosition = () => {
    const getPositionInRange = (value, min, max, startPos, endPos) => {
      const ratio = Math.min(Math.max((value - min) / (max - min), 0), 1)
      return Math.round(startPos + ratio * (endPos - startPos))
    }

    switch (currentRiskLevel) {
      case RISK_LEVELS.VERY_LOW:
        return getPositionInRange(riskValue, 0, 0.25, 1, 4)
      case RISK_LEVELS.LOW:
        return getPositionInRange(riskValue, 0.251, 0.45, 5, 12)
      case RISK_LEVELS.MEDIUM:
        return getPositionInRange(riskValue, 0.451, 0.8, 13, 20)
      case RISK_LEVELS.HIGH:
        return getPositionInRange(riskValue, 0.801, 1.5, 21, 31)
      case RISK_LEVELS.VERY_HIGH:
        return getPositionInRange(riskValue, 1.51, 3.0, 32, 61)
      default:
        return 1
    }
  }

  const activePosition = getActiveBarPosition()
  let currentPosition = 0

  const renderBars = (type, count) => {
    return Array.from({ length: count }).map((_, index) => {
      currentPosition++
      const isActive = currentPosition === activePosition
      const isAfterActive = currentPosition > activePosition
      let barType = isAfterActive ? "default" : type
      if (isEmpty) barType = "default"

      return (
        <Col key={`${type}-${index}`} flex={1}>
          <div className={`risk-bar-item risk-bar-item-${barType} ${isActive ? "active" : ""}`} />
        </Col>
      )
    })
  }

  currentPosition = 0

  const totalBars = 4 + 8 + 8 + 11 + 30 // 61 total bars
  const indicatorPosition = ((activePosition - 1) / totalBars) * 100

  const getTransformIndicatorText = () => {
    if (currentRiskLevel === RISK_LEVELS.VERY_LOW) return 0
    if (currentRiskLevel === RISK_LEVELS.VERY_HIGH && riskValue > 2.5) return 100
    return 50
  }

  return (
    <div className="risk-bar-wrapper">
      <Row gutter={[4, 0]} align="middle" className={"risk-bar"}>
        {renderBars("very_low", 4)}
        {renderBars("low", 8)}
        {renderBars("medium", 8)}
        {renderBars("high", 11)}
        {renderBars("very_high", 30)}
      </Row>

      <Flex
        gap={4}
        vertical
        className={cls("risk-indicator", { neutral: isEmpty })}
        style={{
          left: `${indicatorPosition}%`,
          minWidth: "max-content",
          transform: `translateX(-${getTransformIndicatorText()}%)`
          //transform: `translateX(-100%)`,
        }}
      >
        {!isEmpty && (
          <Text type={getRiskTextColor(currentRiskLevel)}>
            {RISK_LEVEL_TEXT?.[currentRiskLevel]}
          </Text>
        )}
        {isEmpty && (
          <Text type="secondary" strong style={{ color: "#A1A1AA" }}>
            No Stripe connection
          </Text>
        )}
      </Flex>
    </div>
  )
}

export { RISK_LEVELS, RISK_DESCRIPTIONS, RISK_LEVEL_TEXT, getRiskLevel, getRiskTextColor }
export default RiskBar
